// Runtime loader for the talkbot-edge ESM SDK.
//
// The SDK is published as a native ES module whose internal imports are bare
// specifiers ("livekit-client", "onnxruntime-web") that are resolved through an
// import map — exactly how avatar_test.html loads it. Webpack cannot bundle this
// (it would try to resolve those bare imports at build time and rewrite the
// dynamic import), so we replicate the HTML approach: inject an import map and a
// `<script type="module">` that imports the SDK and hands it back via globals.
var IMPORT_MAP = {
    imports: {
        'onnxruntime-web': 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.20.1/dist/ort.min.mjs',
        'onnxruntime-web/webgpu': 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.20.1/dist/ort.webgpu.min.mjs',
        'livekit-client': 'https://cdn.jsdelivr.net/npm/livekit-client@2.19.2/dist/livekit-client.esm.mjs',
    },
};
var READY_EVENT = 'talkbot-edge-ready';
var ERROR_EVENT = 'talkbot-edge-error';
var sdkPromise = null;
/**
 * Load the talkbot-edge SDK once and cache it. Safe to call repeatedly.
 * @param sdkUrl URL of the talkbot-edge.js module.
 */
export function loadEdgeSdk(sdkUrl) {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Edge SDK can only load in the browser'));
    }
    if (window.__talkbotEdge)
        return Promise.resolve(window.__talkbotEdge);
    if (sdkPromise)
        return sdkPromise;
    sdkPromise = new Promise(function (resolve, reject) {
        try {
            // 1) Ensure the import map exists (resolves the SDK's bare deps).
            //    Must be injected before the module script that imports the SDK.
            if (!document.querySelector('script[type="importmap"]')) {
                var im = document.createElement('script');
                im.type = 'importmap';
                im.textContent = JSON.stringify(IMPORT_MAP);
                document.head.appendChild(im);
            }
            var onReady_1 = function () {
                cleanup_1();
                if (window.__talkbotEdge)
                    resolve(window.__talkbotEdge);
                else
                    reject(new Error('Edge SDK loaded but exports are missing'));
            };
            var onError_1 = function () {
                cleanup_1();
                reject(new Error(window.__talkbotEdgeError || 'Failed to load Edge SDK'));
            };
            var cleanup_1 = function () {
                window.removeEventListener(READY_EVENT, onReady_1);
                window.removeEventListener(ERROR_EVENT, onError_1);
            };
            window.addEventListener(READY_EVENT, onReady_1, { once: true });
            window.addEventListener(ERROR_EVENT, onError_1, { once: true });
            // 2) Module script that imports the SDK and exposes it on window.
            //    (Inline module-script import errors don't bubble to onerror, so we
            //    catch inside and dispatch our own events.)
            var mod = document.createElement('script');
            mod.type = 'module';
            mod.textContent = "\n        (async () => {\n          try {\n            const m = await import(".concat(JSON.stringify(sdkUrl), ");\n            window.__talkbotEdge = {\n              TalkbotAvatar: m.TalkbotAvatar,\n              connectLiveKit: m.connectLiveKit,\n            };\n            window.dispatchEvent(new Event(").concat(JSON.stringify(READY_EVENT), "));\n          } catch (e) {\n            window.__talkbotEdgeError = (e && e.message) ? e.message : String(e);\n            window.dispatchEvent(new Event(").concat(JSON.stringify(ERROR_EVENT), "));\n          }\n        })();\n      ");
            document.head.appendChild(mod);
            // Safety timeout.
            setTimeout(function () {
                if (!window.__talkbotEdge) {
                    cleanup_1();
                    reject(new Error('Edge SDK load timed out'));
                }
            }, 30000);
        }
        catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)));
        }
    });
    // Allow a retry if loading failed.
    sdkPromise.catch(function () {
        sdkPromise = null;
    });
    return sdkPromise;
}
